/**
 * Utilidades para trabajar con categorías y años de nacimiento en el frontend
 */

/**
 * Convertir edad a año de nacimiento
 * @param {number} age - Edad en años
 * @param {number} referenceYear - Año de referencia (por defecto actual)
 * @returns {number} Año de nacimiento
 */
export const ageTobirthYear = (age, referenceYear = new Date().getFullYear()) => {
  return referenceYear - age;
};

/**
 * Convertir año de nacimiento a edad
 * @param {number} birthYear - Año de nacimiento
 * @param {number} referenceYear - Año de referencia (por defecto actual)
 * @returns {number} Edad en años
 */
export const birthYearToAge = (birthYear, referenceYear = new Date().getFullYear()) => {
  return referenceYear - birthYear;
};

/**
 * Obtener año de nacimiento desde fecha
 * @param {Date|string} birthDate - Fecha de nacimiento
 * @returns {number} Año de nacimiento
 */
export const getBirthYearFromDate = (birthDate) => {
  return new Date(birthDate).getFullYear();
};

/**
 * Validar rango de año de nacimiento
 * @param {number} startYear - Año inicial
 * @param {number} endYear - Año final
 * @returns {boolean} true si es válido
 */
export const validateBirthYearRange = (startYear, endYear) => {
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 18; // Máximo 18 años
  const maxYear = currentYear - 3;  // Mínimo 3 años
  
  return startYear >= minYear && 
         startYear <= maxYear && 
         endYear >= minYear && 
         endYear <= maxYear && 
         startYear <= endYear;
};

/**
 * Obtener rango de años válidos para categorías
 * @param {number} referenceYear - Año de referencia (por defecto actual)
 * @returns {Object} Rango de años válidos
 */
export const getValidYearRange = (referenceYear = new Date().getFullYear()) => {
  return {
    min: referenceYear - 18, // Máximo 18 años
    max: referenceYear - 3,  // Mínimo 3 años
    current: referenceYear
  };
};

/**
 * Formatear rango de años para mostrar
 * @param {Object} birthYearRange - Rango {start, end}
 * @param {boolean} includeAges - Si incluir edades equivalentes
 * @returns {string} Texto formateado
 */
export const formatBirthYearRange = (birthYearRange, includeAges = false) => {
  if (!birthYearRange || !birthYearRange.start || !birthYearRange.end) {
    return 'Rango no definido';
  }

  let formatted = `${birthYearRange.start}-${birthYearRange.end}`;
  
  if (includeAges) {
    const currentYear = new Date().getFullYear();
    const minAge = currentYear - birthYearRange.end;
    const maxAge = currentYear - birthYearRange.start;
    formatted += ` (${minAge}-${maxAge} años)`;
  }
  
  return formatted;
};

/**
 * Formatear información de categoría para mostrar
 * @param {Object} category - Categoría
 * @returns {Object} Información formateada
 */
export const formatCategoryInfo = (category) => {
  if (!category) return null;

  const genderText = {
    'boys': 'Niños',
    'girls': 'Niñas',
    'mixed': 'Mixto'
  };

  let rangeText = '';
  let rangeType = '';

  // Priorizar birthYearRange si está disponible
  if (category.birthYearRange && category.birthYearRange.start && category.birthYearRange.end) {
    rangeText = `Nacidos: ${category.birthYearRange.start}-${category.birthYearRange.end}`;
    rangeType = 'birthYear';
  } else if (category.ageRange && category.ageRange.min && category.ageRange.max) {
    rangeText = `${category.ageRange.min}-${category.ageRange.max} años`;
    rangeType = 'age';
  }

  return {
    id: category._id || category.id,
    name: category.name,
    description: category.description,
    gender: category.gender,
    genderText: genderText[category.gender] || 'No especificado',
    rangeText,
    rangeType,
    birthYearRange: category.birthYearRange,
    ageRange: category.ageRange,
    color: category.color || '#1976d2',
    icon: category.icon || 'sports_soccer',
    active: category.active,
    formattedName: `${category.name} ${genderText[category.gender] || ''} ${rangeText ? `(${rangeText})` : ''}`.trim()
  };
};

/**
 * Generar opciones de años para formularios
 * @param {number} yearsBack - Años hacia atrás desde el año actual
 * @param {number} yearsForward - Años hacia adelante desde el año actual
 * @returns {Array} Array de objetos {value, label}
 */
export const generateYearOptions = (yearsBack = 18, yearsForward = 0) => {
  const currentYear = new Date().getFullYear();
  const options = [];
  
  for (let year = currentYear + yearsForward; year >= currentYear - yearsBack; year--) {
    options.push({
      value: year,
      label: year.toString()
    });
  }
  
  return options;
};

/**
 * Calcular edad actual desde fecha de nacimiento
 * @param {Date|string} birthDate - Fecha de nacimiento
 * @returns {number} Edad actual en años
 */
export const calculateCurrentAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

/**
 * Validar fecha de nacimiento para categorías deportivas
 * @param {Date|string} birthDate - Fecha de nacimiento
 * @returns {Object} Resultado de validación
 */
export const validateBirthDateForSports = (birthDate) => {
  const birth = new Date(birthDate);
  const today = new Date();
  const age = calculateCurrentAge(birthDate);
  
  const result = {
    valid: true,
    age,
    birthYear: birth.getFullYear(),
    errors: []
  };

  // Verificar que la fecha no sea futura
  if (birth > today) {
    result.valid = false;
    result.errors.push('La fecha de nacimiento no puede ser futura');
  }

  // Verificar edad mínima y máxima
  if (age < 3) {
    result.valid = false;
    result.errors.push('La edad mínima es 3 años');
  }

  if (age > 18) {
    result.valid = false;
    result.errors.push('La edad máxima es 18 años');
  }

  return result;
};

/**
 * Filtrar categorías por año de nacimiento
 * @param {Array} categories - Lista de categorías
 * @param {number} birthYear - Año de nacimiento
 * @returns {Array} Categorías filtradas
 */
export const filterCategoriesByBirthYear = (categories, birthYear) => {
  if (!Array.isArray(categories) || !birthYear) {
    return [];
  }

  return categories.filter(category => {
    if (category.birthYearRange) {
      return birthYear >= category.birthYearRange.start && 
             birthYear <= category.birthYearRange.end;
    }
    
    // Fallback a lógica de edad si no hay birthYearRange
    if (category.ageRange) {
      const currentYear = new Date().getFullYear();
      const age = currentYear - birthYear;
      return age >= category.ageRange.min && age <= category.ageRange.max;
    }
    
    return false;
  });
};

/**
 * Obtener categorías sugeridas para una fecha de nacimiento
 * @param {Array} categories - Lista de categorías
 * @param {Date|string} birthDate - Fecha de nacimiento
 * @param {string} gender - Género (boys, girls, mixed)
 * @returns {Array} Categorías sugeridas
 */
export const getSuggestedCategories = (categories, birthDate, gender = 'mixed') => {
  if (!Array.isArray(categories) || !birthDate) {
    return [];
  }

  const birthYear = getBirthYearFromDate(birthDate);
  const filteredByYear = filterCategoriesByBirthYear(categories, birthYear);

  // Filtrar por género
  return filteredByYear.filter(category => {
    if (category.gender === 'mixed') {
      return true;
    }
    
    if (gender === 'boys' || gender === 'girls') {
      return category.gender === gender || category.gender === 'mixed';
    }
    
    return category.gender === 'mixed';
  });
};

/**
 * Convertir rango de edad a rango de año de nacimiento
 * @param {Object} ageRange - Rango de edad {min, max}
 * @param {number} referenceYear - Año de referencia (por defecto actual)
 * @returns {Object} Rango de año de nacimiento {start, end}
 */
export const ageRangeToBirthYearRange = (ageRange, referenceYear = new Date().getFullYear()) => {
  if (!ageRange || !ageRange.min || !ageRange.max) {
    return null;
  }

  return {
    start: referenceYear - ageRange.max, // Más viejo
    end: referenceYear - ageRange.min    // Más joven
  };
};

/**
 * Convertir rango de año de nacimiento a rango de edad
 * @param {Object} birthYearRange - Rango de año {start, end}
 * @param {number} referenceYear - Año de referencia (por defecto actual)
 * @returns {Object} Rango de edad {min, max}
 */
export const birthYearRangeToAgeRange = (birthYearRange, referenceYear = new Date().getFullYear()) => {
  if (!birthYearRange || !birthYearRange.start || !birthYearRange.end) {
    return null;
  }

  return {
    min: referenceYear - birthYearRange.end,   // Más joven
    max: referenceYear - birthYearRange.start  // Más viejo
  };
};
